// Trigger actions — every type the shipping LoadAction switch constructs. Perform
// returns false to BLOCK (the trigger resumes at this action next pump). Presentation
// (ecoms, cutscenes, objectives screens, alerts, view centering) routes through
// ctx.host; sim state changes (player flags/credits/upgrades/allies, switches, unit
// commands, replicator enables) are exact. game/TriggerActions.cpp (all 1,377 lines).

import { GetGameVar, SetGameVar } from '../sim/gamevar.ts';
import type { SimContext } from '../context.ts';
import { IniScanf } from '../data/ini.ts';
import {
  Trigger,
  ParseNumber,
  ParseArea,
  ParseUnitMask,
  ParseUpgradeMask,
  ParseString,
  GetSideMaskFromCaSideMask,
  GetPlayersListFromCaSideMask,
  knPreserveTriggerTriggerAction,
  knWaitTriggerAction,
  knCenterViewTriggerAction,
  knSetNextMissionTriggerAction,
  knEndMissionTriggerAction,
  knEcomTriggerAction,
  knSetAllowedUnitsTriggerAction,
  knAlliesTriggerAction,
  knSetObjectiveTriggerAction,
  knSetSwitchTriggerAction,
  knDefogAreaTriggerAction,
  knCreateUnitGroupTriggerAction,
  knHuntTriggerAction,
  knCreateRandomUnitGroupTriggerAction,
  knStartCountdownTimerTriggerAction,
  knModifyCountdownTimerTriggerAction,
  knRepairTriggerAction,
  knEnableReplicatorTriggerAction,
  knModifyCreditsTriggerAction,
  knMoveUnitsInAreaTriggerAction,
  knSetFormalObjectiveTextTriggerAction,
  knSetFormalObjectiveStatusTriggerAction,
  knShowObjectivesTriggerAction,
  knSetFormalObjectiveInfoTriggerAction,
  knCutSceneTriggerAction,
  knJumpToMissionTriggerAction,
  knModifyPvarTriggerAction,
  knSetPvarTextTriggerAction,
  knShowAlertTriggerAction,
  knSetAllowedUpgradesTriggerAction,
  knSetUpgradesTriggerAction,
  knWinLoseTypeWin,
  knMoreCloseTypeMore,
  knModifyNumberTypeSet,
  knModifyNumberTypeAdd,
  knModifyNumberTypeSubtract,
} from './triggermgr.ts';
import {
  kcSides,
  kfPlrComputer,
  kfPlrObserver,
  kfPlrUnfulfilled,
  kupgmAdvancedHRC,
  kupgmAdvancedVTS,
} from '../player/side.ts';
import { GetSideMask } from '../types.ts';
import { kfUpgradeHrc, kfUpgradeVts } from '../sm/message.ts';
import { Enum } from '../types.ts';
import { kumMobileUnits } from '../player/masks.ts';
import { kgtReplicator } from '../gob/gobtypes.ts';
import type { ReplicatorGob } from '../gob/replicator.ts';
import { SendMoveAction, MobileUnitGob } from '../gob/mobileunit.ts';
import type { MobileUnitConsts } from '../gob/unitconsts.ts';
import { WcFromTc } from '../math/coords.ts';
import type { Gob } from '../gob/gob.ts';

// game/ht.h:6990-area — keep in sync with level.ts (not imported: level.ts imports the
// trigger layer, and a back-import would make a module cycle through the gob chain)
const kcpgobMax = 768;

// game/res.h:640-643
export const knModifyCountdownTypeStop = 1;
export const knModifyCountdownTypeResume = 2;
export const knModifyCountdownTypeHide = 3;
export const knModifyCountdownTypeShow = 4;

export abstract class TriggerAction {
  /** Set by LoadAction; lets serializers reach host dev hooks (cross-validation). */
  protected ctxLoad?: SimContext;
  Init(_ctx: SimContext, _psz: string): boolean {
    return true;
  }
  // TriggerActions.cpp:200-214 — base actions have no state
  LoadState(_pstm: import('../sm/stream.ts').Stream): boolean {
    return true;
  }
  SaveState(_pstm: import('../sm/stream.ts').Stream): boolean {
    return true;
  }
  abstract Perform(ctx: SimContext, ptgr: Trigger, side: number): boolean;
}

// TriggerActions.cpp:5-193 — the LoadAction switch
export function LoadAction(ctx: SimContext, ptgr: Trigger, pszValue: string): boolean {
  const r = IniScanf(pszValue, '%d,%s');
  if (r.count === 0) return false;
  const nAction = r.values[0] as number;
  const sz = (r.count >= 2 ? r.values[1] : '') as string;

  let pactn: TriggerAction;
  switch (nAction) {
    // (ctxLoad is attached after the switch)
    case knPreserveTriggerTriggerAction:
      pactn = new PreserveTriggerAction();
      break;
    case knWaitTriggerAction:
      pactn = new WaitAction();
      break;
    case knCenterViewTriggerAction:
      pactn = new CenterViewAction();
      break;
    case knSetNextMissionTriggerAction:
      pactn = new SetNextMissionAction();
      break;
    case knEndMissionTriggerAction:
      pactn = new EndMissionAction();
      break;
    case knEcomTriggerAction:
      pactn = new EcomAction();
      break;
    case knSetAllowedUnitsTriggerAction:
      pactn = new SetAllowedUnitsAction();
      break;
    case knAlliesTriggerAction:
      pactn = new AlliesAction();
      break;
    case knSetObjectiveTriggerAction:
      pactn = new SetObjectiveAction();
      break;
    case knSetSwitchTriggerAction:
      pactn = new SetSwitchAction();
      break;
    case knDefogAreaTriggerAction:
      pactn = new DefogAreaAction();
      break;
    case knCreateUnitGroupTriggerAction:
      pactn = new CreateUnitGroupAction();
      break;
    case knHuntTriggerAction:
      pactn = new HuntAction();
      break;
    case knCreateRandomUnitGroupTriggerAction:
      pactn = new CreateRandomUnitGroupAction();
      break;
    case knStartCountdownTimerTriggerAction:
      pactn = new StartCountdownAction();
      break;
    case knModifyCountdownTimerTriggerAction:
      pactn = new ModifyCountdownAction();
      break;
    case knRepairTriggerAction:
      pactn = new RepairAction();
      break;
    case knEnableReplicatorTriggerAction:
      pactn = new EnableReplicatorAction();
      break;
    case knModifyCreditsTriggerAction:
      pactn = new ModifyCreditsAction();
      break;
    case knMoveUnitsInAreaTriggerAction:
      pactn = new MoveUnitsInAreaAction();
      break;
    case knSetFormalObjectiveTextTriggerAction:
      pactn = new SetFormalObjectiveTextAction();
      break;
    case knSetFormalObjectiveStatusTriggerAction:
      pactn = new SetFormalObjectiveStatusAction();
      break;
    case knSetFormalObjectiveInfoTriggerAction:
      pactn = new SetFormalObjectiveInfoAction();
      break;
    case knShowObjectivesTriggerAction:
      pactn = new ShowObjectivesAction();
      break;
    case knCutSceneTriggerAction:
      pactn = new CutSceneAction();
      break;
    case knJumpToMissionTriggerAction:
      pactn = new JumpToMissionAction();
      break;
    case knModifyPvarTriggerAction:
      pactn = new ModifyPvarAction();
      break;
    case knSetPvarTextTriggerAction:
      pactn = new SetPvarTextAction();
      break;
    case knShowAlertTriggerAction:
      pactn = new ShowAlertAction();
      break;
    case knSetAllowedUpgradesTriggerAction:
      pactn = new SetAllowedUpgradesAction();
      break;
    case knSetUpgradesTriggerAction:
      pactn = new SetUpgradesAction();
      break;
    default:
      throw new Error(`LoadAction: unknown action type ${nAction} (C++ asserts)`);
  }

  (pactn as unknown as { ctxLoad?: SimContext }).ctxLoad = ctx;
  if (!pactn.Init(ctx, sz)) return false;
  ptgr.m_pactn.push(pactn); // link in last
  return true;
}

// TriggerActions.cpp:210-216
export class PreserveTriggerAction extends TriggerAction {
  override Perform(_ctx: SimContext, ptgr: Trigger, side: number): boolean {
    ptgr.Arm(side);
    return true;
  }
}

// TriggerActions.cpp:218-280 — per-side blocking wait on the sim tick clock
export class WaitAction extends TriggerAction {
  m_ctWait = 0;
  m_atStartSide: number[] = new Array(kcSides).fill(0);
  m_afWaitingSide: boolean[] = new Array(kcSides).fill(false);

  // TriggerActions.cpp:222-246 (knVerWaitActionState 2) — raw dword[kcSides] start
  // ticks + bool[kcSides] waiting flags, both RLE
  override LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    // dev hook (host-carried): the cross-validation masker canonicalizes the RLE span
    // (TriggerActions.cpp's WaitAction ctor memsets ONLY m_afWaitingSide — the saved
    // m_atStartSide is uninitialized heap until a side actually waits, sim-quirks B18)
    const spanStart = (pstm as unknown as { tell?: () => number }).tell?.() ?? -1;
    const nVer = pstm.ReadByte();
    if (nVer !== 2) return false;
    const at = new Uint8Array(kcSides * 4);
    pstm.ReadBytesRLE(at);
    for (let i = 0; i < kcSides; i++) {
      this.m_atStartSide[i] =
        (at[i * 4] | (at[i * 4 + 1] << 8) | (at[i * 4 + 2] << 16) | (at[i * 4 + 3] << 24)) >>> 0;
    }
    const af = new Uint8Array(kcSides);
    pstm.ReadBytesRLE(af);
    for (let i = 0; i < kcSides; i++) this.m_afWaitingSide[i] = af[i] !== 0;
    (
      this.ctxLoad?.host as unknown as {
        waitActionSpans?: { start: number; end: number; at: Uint8Array; af: Uint8Array }[];
      }
    )?.waitActionSpans?.push({
      start: spanStart,
      end: (pstm as unknown as { tell?: () => number }).tell?.() ?? -1,
      at: at.slice(),
      af: af.slice(),
    });
    return pstm.IsSuccess();
  }

  override SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(2);
    const at = new Uint8Array(kcSides * 4);
    for (let i = 0; i < kcSides; i++) {
      const v = this.m_atStartSide[i] >>> 0;
      at[i * 4] = v & 0xff;
      at[i * 4 + 1] = (v >> 8) & 0xff;
      at[i * 4 + 2] = (v >> 16) & 0xff;
      at[i * 4 + 3] = (v >> 24) & 0xff;
    }
    pstm.WriteBytesRLE(at);
    const af = new Uint8Array(kcSides);
    for (let i = 0; i < kcSides; i++) af[i] = this.m_afWaitingSide[i] ? 1 : 0;
    pstm.WriteBytesRLE(af);
    return pstm.IsSuccess();
  }

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_ctWait = out.n * 100;
    return true;
  }

  override Perform(ctx: SimContext, _ptgr: Trigger, side: number): boolean {
    const t = ctx.sim!.GetTickCount();
    if (this.m_afWaitingSide[side]) {
      if (t - this.m_atStartSide[side] >= this.m_ctWait) {
        this.m_afWaitingSide[side] = false;
        return true;
      }
      return false;
    }
    this.m_afWaitingSide[side] = true;
    this.m_atStartSide[side] = t >>> 0;
    return false;
  }
}

// TriggerActions.cpp:292-317 — local-player view scroll: presentation
export class CenterViewAction extends TriggerAction {
  m_nArea = 0;

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseArea(pp, out)) return false;
    this.m_nArea = out.n;
    return true;
  }

  override Perform(ctx: SimContext, _ptgr: Trigger, side: number): boolean {
    if (ctx.plrLocal === null || side !== ctx.plrLocal.GetSide()) return true;
    ctx.host.CenterViewOnArea(this.m_nArea);
    return true;
  }
}

// TriggerActions.cpp:319-340
export class SetNextMissionAction extends TriggerAction {
  m_szLevel = '';

  override Init(_ctx: SimContext, psz: string): boolean {
    this.m_szLevel = psz;
    return true;
  }

  override Perform(ctx: SimContext, _ptgr: Trigger, side: number): boolean {
    if (ctx.plrLocal === null || side !== ctx.plrLocal.GetSide()) return true;
    ctx.host.SetNextLevel(this.m_szLevel === '[none]' ? '' : this.m_szLevel);
    return true;
  }
}

// TriggerActions.cpp:342-442 — the SIM effect in MP: the ended player becomes an
// observer + computer (tracked flags); alerts/objective screens/events are host
export class EndMissionAction extends TriggerAction {
  m_nWinLose = 0;

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_nWinLose = out.n;
    return true;
  }

  override Perform(ctx: SimContext, _ptgr: Trigger, side: number): boolean {
    // SP: only the local player's side may end the mission (bad scripting otherwise)
    if (!ctx.fMultiplayer) {
      if (ctx.plrLocal === null || side !== ctx.plrLocal.GetSide()) return true;
      ctx.sim!.GetLevel()!.GetTriggerMgr().Enable(false);
      ctx.host.EndMission(this.m_nWinLose === knWinLoseTypeWin, side);
      return true;
    }

    const pplr = ctx.plrm.GetPlayer(side)!;
    if ((pplr.GetFlags() & (kfPlrUnfulfilled | kfPlrObserver)) === 0) {
      ctx.host.ShowAlert(
        this.m_nWinLose === knWinLoseTypeWin
          ? `${pplr.GetName()} is victorious!`
          : `${pplr.GetName()} has been defeated!`,
      );

      // This player is now out, and an observer
      pplr.SetFlags(pplr.GetFlags() | kfPlrObserver | kfPlrComputer);

      // the end-mission/check-game-over events are async host concerns
      ctx.host.NotifyEndMission(side, this.m_nWinLose === knWinLoseTypeWin);
    }
    return true;
  }
}

// TriggerActions.cpp:444-523 — SP-only ecom display; blocks while a build form is up
export class EcomAction extends TriggerAction {
  m_nBackground = 0;
  m_fMore = false;
  m_nCharFrom = 0;
  m_nCharTo = 0;
  m_pszMessage = '';

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_nBackground = out.n;
    if (!ParseNumber(pp, out)) return false;
    this.m_fMore = out.n === knMoreCloseTypeMore;
    if (!ParseNumber(pp, out)) return false;
    this.m_nCharFrom = out.n;
    if (!ParseNumber(pp, out)) return false;
    this.m_nCharTo = out.n;
    this.m_pszMessage = pp.psz;
    return true;
  }

  override Perform(ctx: SimContext, _ptgr: Trigger, _side: number): boolean {
    if (ctx.fMultiplayer) return true;
    // SEAM NOTE: the ONE host return value the sim branches on — a build form being
    // up defers the EcomAction (Perform false = the trigger retries next pump). The
    // C++ reads live UI state here too (TriggerActions.cpp:509). Exempt from the
    // no-host-branching rule because the MP arm above already returned: only SP
    // (single machine, no lockstep peer) ever reaches this read.
    if (ctx.host.EcomSuppressed()) return false;
    ctx.host.Ecom(
      this.m_nCharFrom,
      this.m_nCharTo,
      this.m_pszMessage,
      this.m_nBackground,
      this.m_fMore,
    );
    return true;
  }
}

// TriggerActions.cpp:525-543
export class SetAllowedUnitsAction extends TriggerAction {
  m_wfCaSideMask = 0;
  m_um = 0;

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_wfCaSideMask = out.n;
    if (!ParseUnitMask(pp, out)) return false;
    this.m_um = out.n;
    return true;
  }

  override Perform(ctx: SimContext, _ptgr: Trigger, side: number): boolean {
    for (const p of GetPlayersListFromCaSideMask(ctx, side, this.m_wfCaSideMask)) {
      p.SetAllowedUnits(this.m_um);
    }
    return true;
  }
}

// TriggerActions.cpp:545-563
export class SetAllowedUpgradesAction extends TriggerAction {
  m_wfCaSideMask = 0;
  m_upgm = 0;

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_wfCaSideMask = out.n;
    if (!ParseUpgradeMask(pp, out)) return false;
    this.m_upgm = out.n;
    return true;
  }

  override Perform(ctx: SimContext, _ptgr: Trigger, side: number): boolean {
    for (const p of GetPlayersListFromCaSideMask(ctx, side, this.m_wfCaSideMask)) {
      p.SetAllowedUpgrades(this.m_upgm);
    }
    return true;
  }
}

// TriggerActions.cpp:565-589
export class SetUpgradesAction extends TriggerAction {
  m_wfCaSideMask = 0;
  m_upgm = 0;

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_wfCaSideMask = out.n;
    if (!ParseUpgradeMask(pp, out)) return false;
    this.m_upgm = out.n;
    return true;
  }

  override Perform(ctx: SimContext, _ptgr: Trigger, side: number): boolean {
    let wfUpgrade = 0;
    if (this.m_upgm & kupgmAdvancedHRC) wfUpgrade |= kfUpgradeHrc;
    if (this.m_upgm & kupgmAdvancedVTS) wfUpgrade |= kfUpgradeVts;
    for (const p of GetPlayersListFromCaSideMask(ctx, side, this.m_wfCaSideMask)) {
      p.SetUpgrades(wfUpgrade);
    }
    return true;
  }
}

// TriggerActions.cpp:591-612
export class AlliesAction extends TriggerAction {
  m_wfCaSideMaskA = 0;
  m_wfCaSideMaskB = 0;

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_wfCaSideMaskA = out.n;
    if (!ParseNumber(pp, out)) return false;
    this.m_wfCaSideMaskB = out.n;
    return true;
  }

  override Perform(ctx: SimContext, _ptgr: Trigger, side: number): boolean {
    const sidmAllies = GetSideMaskFromCaSideMask(ctx, side, this.m_wfCaSideMaskB);
    const applr = GetPlayersListFromCaSideMask(ctx, side, this.m_wfCaSideMaskA);
    ctx.plrm.SetAllies(applr, sidmAllies);
    return true;
  }
}

// TriggerActions.cpp:614-647
export class SetObjectiveAction extends TriggerAction {
  m_wfCaSideMask = 0;
  m_szObjective = '';

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_wfCaSideMask = out.n;
    this.m_szObjective = pp.psz;
    return true;
  }

  override Perform(ctx: SimContext, _ptgr: Trigger, side: number): boolean {
    for (const p of GetPlayersListFromCaSideMask(ctx, side, this.m_wfCaSideMask)) {
      p.SetObjective(this.m_szObjective);
    }
    return true;
  }
}

// TriggerActions.cpp:649-668
export class SetSwitchAction extends TriggerAction {
  m_iSwitch = 0;
  m_fOn = false;

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_iSwitch = out.n;
    if (!ParseNumber(pp, out)) return false;
    this.m_fOn = out.n === 1;
    return true;
  }

  override Perform(ctx: SimContext): boolean {
    ctx.sim!.GetLevel()!.GetTriggerMgr().SetSwitch(this.m_iSwitch, this.m_fOn);
    return true;
  }
}

// TriggerActions.cpp:670-693 — fog reveal over an area (SP fog slice; MP fogmaps are
// machine-local and the galaxite nibble is unaffected by Reveal)
export class DefogAreaAction extends TriggerAction {
  m_nArea = 0;

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseArea(pp, out)) return false;
    this.m_nArea = out.n;
    return true;
  }

  // TriggerActions.cpp:676-693 — the local-side gate is #if 0'd out: EVERY side's
  // Perform reveals the rect (fog is per-client, but the reveal is unconditional).
  override Perform(ctx: SimContext, _ptgr: Trigger, side: number): boolean {
    const trc = ctx.gobm.GetAreaRect(this.m_nArea, side);
    ctx.fogm!.Reveal(trc);
    return true;
  }
}

// TriggerActions.cpp:695-706
export class CreateUnitGroupAction extends TriggerAction {
  m_nUnitGroup = 0;

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_nUnitGroup = out.n;
    return true;
  }

  override Perform(ctx: SimContext): boolean {
    (
      ctx.sim!.GetLevel()!.GetUnitGroupMgr() as unknown as { ActivateUnitGroup(n: number): void }
    ).ActivateUnitGroup(this.m_nUnitGroup);
    return true;
  }
}

// TriggerActions.cpp:708-719
export class CreateRandomUnitGroupAction extends TriggerAction {
  override Perform(ctx: SimContext): boolean {
    (
      ctx.sim!.GetLevel()!.GetUnitGroupMgr() as unknown as { ActivateRandomUnitGroup(): void }
    ).ActivateRandomUnitGroup();
    return true;
  }
}

// TriggerActions.cpp:720-753 — the C++ body is EMPTY (UNDONE); parse args, do nothing
export class HuntAction extends TriggerAction {
  m_um1 = 0;
  m_wfCaSideMask1 = 0;
  m_um2 = 0;
  m_wfCaSideMask2 = 0;

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseUnitMask(pp, out)) return false;
    this.m_um1 = out.n;
    if (!ParseNumber(pp, out)) return false;
    this.m_wfCaSideMask1 = out.n;
    if (!ParseUnitMask(pp, out)) return false;
    this.m_um2 = out.n;
    if (!ParseNumber(pp, out)) return false;
    this.m_wfCaSideMask2 = out.n;
    return true;
  }

  override Perform(): boolean {
    return true;
  }
}

// TriggerActions.cpp:755-788
export class StartCountdownAction extends TriggerAction {
  m_nSecs = 0;
  m_szCountdown = '';

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_nSecs = out.n;
    this.m_szCountdown = pp.psz;
    return true;
  }

  override Perform(ctx: SimContext): boolean {
    const pcdt = ctx.sim!.GetLevel()!.GetTriggerMgr().GetCountdownTimer();
    pcdt.SetTimer(this.m_nSecs, this.m_szCountdown);
    pcdt.StartTimer(true);
    return true;
  }
}

// TriggerActions.cpp:790-820
export class ModifyCountdownAction extends TriggerAction {
  m_nAction = 0;

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_nAction = out.n;
    return true;
  }

  override Perform(ctx: SimContext): boolean {
    const pct = ctx.sim!.GetLevel()!.GetTriggerMgr().GetCountdownTimer();
    switch (this.m_nAction) {
      case knModifyCountdownTypeStop:
        pct.StartTimer(false);
        break;
      case knModifyCountdownTypeResume:
        pct.StartTimer(true);
        break;
      case knModifyCountdownTypeHide:
        pct.ShowTimer(false);
        break;
      case knModifyCountdownTypeShow:
        pct.ShowTimer(true);
        break;
    }
    return true;
  }
}

// TriggerActions.cpp:844-870
export class RepairAction extends TriggerAction {
  m_wfCaSideMask = 0;
  m_fOn = false;

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_wfCaSideMask = out.n;
    if (!ParseNumber(pp, out)) return false;
    this.m_fOn = out.n === 1;
    return true;
  }

  override Perform(ctx: SimContext, _ptgr: Trigger, side: number): boolean {
    for (const p of GetPlayersListFromCaSideMask(ctx, side, this.m_wfCaSideMask)) {
      p.Repair(this.m_fOn);
    }
    return true;
  }
}

// TriggerActions.cpp:872-899
export class EnableReplicatorAction extends TriggerAction {
  m_wfCaSideMask = 0;
  m_fOn = false;

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_wfCaSideMask = out.n;
    if (!ParseNumber(pp, out)) return false;
    this.m_fOn = out.n === 1;
    return true;
  }

  override Perform(ctx: SimContext, _ptgr: Trigger, side: number): boolean {
    const sidm = GetSideMaskFromCaSideMask(ctx, side, this.m_wfCaSideMask);
    for (let pgob = ctx.gobm.GetFirstGob(); pgob !== null; pgob = ctx.gobm.GetNextGob(pgob)) {
      if (pgob.GetType() === kgtReplicator && (sidm & GetSideMask(pgob.GetSide())) !== 0) {
        (pgob as ReplicatorGob).Enable(this.m_fOn);
      }
    }
    return true;
  }
}

// TriggerActions.cpp:901-948
export class ModifyCreditsAction extends TriggerAction {
  m_nAction = 0;
  m_wfCaSideMask = 0;
  m_nAmount = 0;

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_nAction = out.n;
    if (!ParseNumber(pp, out)) return false;
    this.m_wfCaSideMask = out.n;
    if (!ParseNumber(pp, out)) return false;
    this.m_nAmount = out.n;
    return true;
  }

  override Perform(ctx: SimContext, _ptgr: Trigger, side: number): boolean {
    for (const pplr of GetPlayersListFromCaSideMask(ctx, side, this.m_wfCaSideMask)) {
      switch (this.m_nAction) {
        case knModifyNumberTypeSet:
          pplr.SetCredits(this.m_nAmount, true);
          break;
        case knModifyNumberTypeAdd: {
          let nCredits = pplr.GetCredits() + this.m_nAmount;
          if (nCredits < 0) nCredits = 0;
          pplr.SetCredits(nCredits, true);
          break;
        }
        case knModifyNumberTypeSubtract: {
          let nCredits = pplr.GetCredits() - this.m_nAmount;
          if (nCredits < 0) nCredits = 0;
          pplr.SetCredits(nCredits, true);
          break;
        }
      }
    }
    return true;
  }
}

// SimUI.cpp:1664-1693 — unit count -> formation radius (sim data: feeds move targets)
const gmpcUnitsToRadius = [
  0, 0, 1, 1, 1, 1, 2, 2, 2, 2, 2, 2, 2, 2, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 4, 4, 4,
  4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5,
  5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6,
  6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7,
  7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8,
  8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8,
  9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9,
  9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9, 10, 10,
];

// SimUI.cpp:1684-1693
export function RadiusFromUnitCount(cUnits: number): number {
  if (cUnits >= gmpcUnitsToRadius.length) return gmpcUnitsToRadius[gmpcUnitsToRadius.length - 1];
  return gmpcUnitsToRadius[cUnits];
}

// TriggerActions.cpp:1028-1060 — min group speed + formation radius, one move per unit
export function MoveUnitsToArea(
  ctx: SimContext,
  apmunt: MobileUnitGob[],
  trc: { left: number; top: number; right: number; bottom: number },
): void {
  let wcMoveDistPerUpdate = 0x7fffffff; // kwcMax
  for (const pmunt of apmunt) {
    const pmuntc = pmunt.GetConsts() as MobileUnitConsts;
    if (pmuntc.GetMoveDistPerUpdate() < wcMoveDistPerUpdate) {
      wcMoveDistPerUpdate = pmuntc.GetMoveDistPerUpdate();
    }
  }

  const tcRadius = RadiusFromUnitCount(apmunt.length) + 1;
  // TRect::GetCenter
  const xCenter = Math.trunc((trc.left + trc.right) / 2);
  const yCenter = Math.trunc((trc.top + trc.bottom) / 2);
  for (const pmunt of apmunt) {
    SendMoveAction(
      ctx,
      pmunt.GetId(),
      WcFromTc(xCenter),
      WcFromTc(yCenter),
      tcRadius,
      wcMoveDistPerUpdate,
    );
  }
}

// TriggerActions.cpp:978-1026
export class MoveUnitsInAreaAction extends TriggerAction {
  m_wfCaSideMask = 0;
  m_um = 0;
  m_nAreaSrc = 0;
  m_nAreaDst = 0;

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_wfCaSideMask = out.n;
    if (!ParseUnitMask(pp, out)) return false;
    this.m_um = out.n;
    if (!ParseArea(pp, out)) return false;
    this.m_nAreaSrc = out.n;
    if (!ParseArea(pp, out)) return false;
    this.m_nAreaDst = out.n;
    return true;
  }

  override Perform(ctx: SimContext, _ptgr: Trigger, side: number): boolean {
    const apgob: Gob[] = [];
    const enm = new Enum();
    const umFind = (this.m_um & kumMobileUnits) >>> 0;
    const sidmFind = GetSideMaskFromCaSideMask(ctx, side, this.m_wfCaSideMask);
    let pgob: Gob | null;
    while ((pgob = ctx.gobm.EnumGobsInArea(enm, this.m_nAreaSrc, sidmFind, umFind)) !== null) {
      apgob.push(pgob);
      if (apgob.length >= Math.trunc(kcpgobMax / 2)) break;
    }

    if (apgob.length > 0) {
      const trc = ctx.gobm.GetAreaRect(this.m_nAreaDst, side);
      MoveUnitsToArea(ctx, apgob as MobileUnitGob[], trc);
    }
    return true;
  }
}

// TriggerActions.cpp:1062-1099
export class SetFormalObjectiveTextAction extends TriggerAction {
  m_iObjective = 0;
  m_szObjective = '';

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_iObjective = out.n;
    this.m_szObjective = pp.psz;
    return true;
  }

  override Perform(ctx: SimContext, _ptgr: Trigger, side: number): boolean {
    ctx.plrm.GetPlayer(side)!.SetFormalObjectiveText(this.m_iObjective, this.m_szObjective);
    return true;
  }
}

// TriggerActions.cpp:1101-1125
export class SetFormalObjectiveStatusAction extends TriggerAction {
  m_iObjective = 0;
  m_szStatus = '';

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_iObjective = out.n;
    this.m_szStatus = pp.psz;
    return true;
  }

  override Perform(ctx: SimContext, _ptgr: Trigger, side: number): boolean {
    ctx.plrm.GetPlayer(side)!.SetFormalObjectiveStatus(this.m_iObjective, this.m_szStatus);
    return true;
  }
}

// TriggerActions.cpp:1170-1192
export class SetFormalObjectiveInfoAction extends TriggerAction {
  m_szInfo = '';

  override Init(_ctx: SimContext, psz: string): boolean {
    this.m_szInfo = psz;
    return true;
  }

  override Perform(ctx: SimContext, _ptgr: Trigger, side: number): boolean {
    ctx.plrm.GetPlayer(side)!.SetFormalObjectiveInfo(this.m_szInfo);
    return true;
  }
}

// TriggerActions.cpp:1127-1162 — objectives screen (host); the MP arm marks the action
// complete FIRST so the async display can't re-enter it
export class ShowObjectivesAction extends TriggerAction {
  override Perform(ctx: SimContext, ptgr: Trigger, side: number): boolean {
    if (ctx.plrLocal === null || side !== ctx.plrLocal.GetSide()) return true;
    if (ctx.fMultiplayer) ptgr.SetCurrentActionComplete(side);
    ctx.host.ShowObjectives(side);
    return true;
  }
}

// TriggerActions.cpp:1194-1227 — SP-only cutscene (host)
export class CutSceneAction extends TriggerAction {
  m_pszMessage = '';

  override Init(_ctx: SimContext, psz: string): boolean {
    this.m_pszMessage = psz;
    return true;
  }

  override Perform(ctx: SimContext): boolean {
    if (ctx.fMultiplayer) return true;
    ctx.host.CutScene(this.m_pszMessage);
    return true;
  }
}

// TriggerActions.cpp:1229-1261 — SP-only jump (host event); disables further triggers
export class JumpToMissionAction extends TriggerAction {
  m_szLevel = '';

  override Init(_ctx: SimContext, psz: string): boolean {
    this.m_szLevel = psz;
    return true;
  }

  override Perform(ctx: SimContext): boolean {
    if (ctx.fMultiplayer) return true;
    ctx.host.SetNextLevel(this.m_szLevel);
    ctx.sim!.GetLevel()!.GetTriggerMgr().Enable(false);
    ctx.host.JumpToMission(this.m_szLevel);
    return true;
  }
}

// TriggerActions.cpp:1263-1345 — persistent profile vars (the Set arm's read is #if 0)
export class ModifyPvarAction extends TriggerAction {
  m_szName = '';
  m_nAction = 0;
  m_nAmount = 0;

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const outS = { s: '' };
    if (!ParseString(pp, outS)) return false;
    this.m_szName = outS.s;
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_nAction = out.n;
    if (!ParseNumber(pp, out)) return false;
    this.m_nAmount = out.n;
    return true;
  }

  override Perform(ctx: SimContext): boolean {
    // TriggerActions.cpp:1286-1305 reads via Game::GetVar (sysvar arm + MTF relink)
    let nAmount = this.m_nAmount;
    const cur = GetGameVar(ctx, this.m_szName);
    switch (this.m_nAction) {
      case knModifyNumberTypeAdd:
        if (cur !== null) nAmount = (parseInt(cur, 10) | 0 || 0) + this.m_nAmount;
        break;
      case knModifyNumberTypeSubtract:
        if (cur !== null) nAmount = (parseInt(cur, 10) | 0 || 0) - this.m_nAmount;
        break;
      case knModifyNumberTypeSet:
        break; // the read-back is #if 0 in C++ ("Not in v1.1 by accident")
    }
    SetGameVar(ctx, this.m_szName, String(nAmount));
    return true;
  }
}

// TriggerActions.cpp:1347-1362
export class SetPvarTextAction extends TriggerAction {
  m_szName = '';
  m_szValue = '';

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const outS = { s: '' };
    if (!ParseString(pp, outS)) return false;
    this.m_szName = outS.s;
    this.m_szValue = pp.psz;
    return true;
  }

  override Perform(ctx: SimContext): boolean {
    SetGameVar(ctx, this.m_szName, this.m_szValue);
    return true;
  }
}

// TriggerActions.cpp:1364-1376
export class ShowAlertAction extends TriggerAction {
  m_szAlert = '';

  override Init(_ctx: SimContext, psz: string): boolean {
    this.m_szAlert = psz;
    return true;
  }

  override Perform(ctx: SimContext): boolean {
    ctx.host.ShowAlert(this.m_szAlert);
    return true;
  }
}
